import {
  ClipVideoPlayer as ClipPlayer,
  CommentPortalSlot,
  EmbedVideoPlayer as EmbedPlayer,
  SlackEmbedVideoPlayer,
  FeedPlayer,
  analyticsInit,
  useMakeClip,
  usePlayerFromContext,
  useVideoContext,
} from '@loomhq/createhq/dist/video-player/';
import { VideoGlobalContainer } from '@loomhq/createhq/dist/video-player/components/video-global-container';
import { Container } from '@loomhq/lens';
import React from 'react';

import { ROLLOUT_AT_MENTION_COMMENT } from 'constants/featureFlag';
import { getShowCaptions } from 'utilities/localStorage';

import * as analytics from '../../utilities/analytics';
import CommentsOverlay from './comments-overlay';
import CommentInput from './comments/CommentInput';
import CommentsHandler from './CommentsHandler';
import useMuteFillerWords from './filler-volume-control/mute-filler-words';
import ReactionsHandler from './ReactionsHandler';
import { useCaptionsUrlQuery, useHandleSource } from './hooks';
import { useInitVideoSession } from './video-session/analytics-hooks';
import FeatureFlaggedComponent from 'components/feature-flag-wrapper';

import './styles.module.less';

const CommentPortalForm = () => {
  const player = usePlayerFromContext();

  const handleClose = () => {
    player?.commentFormToggle();
  };

  return (
    <CommentPortalSlot>
      <Container paddingY="small" width="100%" height="100%">
        <CommentInput inPlayer={true} closeReply={handleClose} />
      </Container>
    </CommentPortalSlot>
  );
};

// TODO (claudio): refactor for composition
// as soon as we merge some current work to avoid nasty git conflicts
// all these props will go away
export function EmbedVideoPlayer({
  feedPlayer,
  slackPlayer,
  isActive = false,
}: {
  feedPlayer?: boolean;
  slackPlayer?: boolean;
  isActive?: boolean;
}): JSX.Element {
  const { video } = useVideoContext();

  // start autoloading in slack
  // since our iframe is loaded
  // after clicking play on their pre-play screen
  useHandleSource(video, slackPlayer);
  useInitVideoSession(video);
  useMuteFillerWords(video);

  let Player = EmbedPlayer;

  if (feedPlayer) {
    Player = FeedPlayer;
  }

  if (slackPlayer) {
    Player = SlackEmbedVideoPlayer;
  }

  return (
    <>
      <VideoGlobalContainer>
        <CommentsOverlay />
        <Player isActive={isActive} />
      </VideoGlobalContainer>

      <LoadCaptions videoId={video.modelId} />
      <FeatureFlaggedComponent
        flag={ROLLOUT_AT_MENTION_COMMENT}
        activationValues={[true]}
      >
        <CommentPortalForm />
      </FeatureFlaggedComponent>
      {video.commentsEnabled && <CommentsHandler />}
      {video.reactionsEnabled && <ReactionsHandler />}
      <AnalyticsLoader />
    </>
  );
}

export function ViewfinderVideoPlayer({
  pause,
}: {
  fullScreen?: boolean;
  pause?: boolean;
}): JSX.Element {
  useImperativePlayback(pause);

  return <EmbedVideoPlayer />;
}

export function ClipVideoPlayer({
  clip,
}: {
  clip: [number, number];
}): JSX.Element {
  const { video } = useVideoContext();

  useHandleSource(video);
  useMakeClip(video.id, clip);

  // TODO: VV: Should the clip player have a way to toggle captions?
  const captionsVisibilityInStorage = getShowCaptions();

  return (
    <>
      <VideoGlobalContainer>
        {captionsVisibilityInStorage && (
          <LoadCaptions videoId={video.modelId} />
        )}
        <ClipPlayer />
      </VideoGlobalContainer>
      <AnalyticsLoader />
    </>
  );
}

function LoadCaptions({ videoId }: { videoId?: string }) {
  useCaptionsUrlQuery(videoId);

  return null;
}

const freshPlayerAnalytics = {
  track: (name, payload) => {
    analytics.track(name, { ...payload, freshPlayer: true });
  },
};

const AnalyticsLoader = () => {
  React.useEffect(() => {
    analyticsInit(freshPlayerAnalytics);
  }, []);

  return null;
};

function useImperativePlayback(pause?: boolean) {
  const player = usePlayerFromContext();

  React.useEffect(() => {
    if (!player) {
      return;
    }

    pause ? player.pause() : player.safePlay();
  }, [player, pause]);
}
